import { Text, Heading, Flex, Progress, Button, Box } from "@chakra-ui/react";
import { QuizStatus, quizColors } from "../../hooks/use-guide";
import LanguageSelector from "./language-selector";

interface Props {
  quizStatus: QuizStatus;
  setQuizStatus: (status: QuizStatus) => void;
  selectedUserLanguage: string | null;
  setSelectedUserLanguage: (lang: string | null) => void;
  selectedPracticeLanguage: string | null;
  setSelectedPracticeLanguage: (lang: string | null) => void;
  onStart: () => void;
}

export default function LandingWelcome(props: Props) {
  const onCancel = () => {
    props.setQuizStatus("init");
    props.setSelectedPracticeLanguage(null);
    props.setSelectedUserLanguage(null);
  };

  return (
    <>
      <Heading
        letterSpacing={"normal"}
        fontWeight="extrabold"
        lineHeight={"2xl"}
        textAlign={"center"}
        mt={10}
        as="h1"
      >
        <Box color="brand.gray.700" fontSize="lg">
          Your Language, Your Journey{" "}
        </Box>
        <Box color={quizColors.primary} fontSize={{ base: "xl", md: "2xl" }}>
          An Interactive Beginner´s Path
        </Box>
      </Heading>

      {props.quizStatus !== "pending" && (
        <Text fontSize="sm" mt={5} maxW="400px" ml="auto" mr="auto" textAlign={"center"}>
          Try our free language tutorial that will guide you through the basics of a language of
          your choosing.
        </Text>
      )}

      <Flex
        flexDir={"column"}
        justifyContent="center"
        my={10}
        w="400px"
        maxW={"100%"}
        ml="auto"
        mr="auto"
        flex="2"
      >
        {props.quizStatus === "error" && (
          <Box textAlign={"center"}>
            <Text fontWeight={"bold"} color="brand.red.400" mb={2}>
              Hmm... looks like an error occurred
            </Text>
            <Text fontSize="xs" fontWeight={"bold"} color="brand.gray.400">
              Please refresh and try again in a few moments.
            </Text>
          </Box>
        )}

        {props.quizStatus === "pending" && (
          <Flex flexDir="column" textAlign={"center"} minH="150px" justifyContent={"center"}>
            <Box w="100%" borderRadius="50%" overflow="hidden" mt={5} mb={7}>
              <Progress size="xs" isIndeterminate />
            </Box>
            <Text fontWeight="bold" color="gray.600" mt={3}>
              Hang tight, we are paving the path
            </Text>
            <Text fontSize="xs" fontWeight="bold" color="gray.400" mt={2}>
              {`Looks like we don't have this language combination yet.`} <br />
              {`Come back a little later and we'll have it ready for you.`}
            </Text>

            <Text
              fontSize="2xs"
              mt={12}
              color="gray.400"
              textDecor={"underline"}
              onClick={onCancel}
              cursor="pointer"
            >
              Cancel
            </Text>
          </Flex>
        )}

        {(props.quizStatus === "init" ||
          props.quizStatus === "ready" ||
          props.quizStatus === "loading") && (
          <>
            <Box mb={5}>
              <Text color="gray.500" mb={1} fontSize="xs" fontWeight={"bold"}>
                Your native language
              </Text>
              <LanguageSelector
                id="user"
                value={props.selectedUserLanguage || undefined}
                onChange={(v) => props.setSelectedUserLanguage(v.target.value)}
              />
            </Box>

            <Box>
              <Text color="gray.500" mb={1} fontSize="xs" fontWeight={"bold"}>
                The language you want to learn
              </Text>
              <LanguageSelector
                id="practice"
                value={props.selectedPracticeLanguage || undefined}
                onChange={(v) => props.setSelectedPracticeLanguage(v.target.value)}
              />
            </Box>
          </>
        )}

        {props.selectedPracticeLanguage &&
        props.selectedUserLanguage &&
        (props.quizStatus === "ready" || props.quizStatus === "loading") ? (
          <Button
            colorScheme="brand.secondary"
            backgroundColor={quizColors.secondary}
            variant="solid"
            mt={5}
            mb={10}
            w={"100%"}
            isLoading={props.quizStatus === "loading"}
            onClick={props.onStart}
            id="start-button"
          >
            Start
          </Button>
        ) : (
          props.quizStatus !== "pending" && (
            <Box color="gray.300" mt={7} fontSize="md" fontWeight="bold" textAlign={"center"}>
              Select languages to get started
            </Box>
          )
        )}
      </Flex>
    </>
  );
}
